import { useState, useEffect, useCallback, useRef } from 'react';
import type { FeatureModule, FeatureMode, FeatureModeChangeRequest } from '@/types/features';
import { dataService } from '@/services/data-service';

const STORAGE_KEY = 'feature-settings';
const CURRENT_VERSION = '1.3.0'; // Incremented to update Compliance and Dashboard to support live mode

// Initialize default feature modules
// apiRequired: true means the module only has demo data and cannot be switched to live mode
// Based on analysis of data-service.ts and backend controllers:
// - Modules with full API support: assets, locations, categories, equipment, disposalReasons, depreciation, revaluation, additions, auditTrail, audits, assetHistory
// - Modules with demo-only data: dashboard, checkinout, compliance, insights, importAssets, importTransactions, help
const getDefaultModules = (): Record<string, FeatureModule> => ({
  dashboard: {
    id: 'dashboard',
    name: 'Dashboard',
    description: 'Central command centre for asset overview and key metrics',
    icon: 'LayoutDashboard',
    mode: 'live',
    status: 'available',
    category: 'core-operations',
    apiRequired: false, // Dashboard now has full demo/live mode support
    documentationUrl: '/docs/dashboard',
  },
  assets: {
    id: 'assets',
    name: 'Assets',
    description: 'Complete asset registry and lifecycle management',
    icon: 'Package',
    mode: 'live',
    status: 'available',
    category: 'core-operations',
    apiRequired: false, // Full API support via AssetsController
    documentationUrl: '/docs/assets',
  },
  additions: {
    id: 'additions',
    name: 'Additions',
    description: 'Track new asset acquisitions and entries',
    icon: 'PlusCircle',
    mode: 'live',
    status: 'available',
    category: 'transaction-management',
    apiRequired: false, // Full API support via AdditionBatchesController
  },
  depreciation: {
    id: 'depreciation',
    name: 'Depreciation',
    description: 'Automated depreciation calculations and schedules',
    icon: 'TrendingDown',
    mode: 'live',
    status: 'available',
    category: 'transaction-management',
    apiRequired: false, // Full API support via DepreciationController/DepreciationBatchesController
  },
  revaluation: {
    id: 'revaluation',
    name: 'Revaluation',
    description: 'Asset value adjustments and market valuations',
    icon: 'RefreshCw',
    mode: 'live',
    status: 'available',
    category: 'transaction-management',
    apiRequired: false, // Full API support via RevaluationBatchesController
  },
  checkinout: {
    id: 'checkinout',
    name: 'Check-In / Check-Out',
    description: 'Asset assignment and tracking system',
    icon: 'ArrowLeftRight',
    mode: 'demo',
    status: 'available',
    category: 'operational-tools',
    apiRequired: true, // No backend controller found - demo only
  },
  compliance: {
    id: 'compliance',
    name: 'Compliance',
    description: 'Regulatory compliance monitoring and alerts',
    icon: 'Shield',
    mode: 'live',
    status: 'available',
    category: 'operational-tools',
    apiRequired: false, // Full API support via ComplianceController
  },
  audits: {
    id: 'audits',
    name: 'Audits',
    description: 'Physical asset audits and verification workflows',
    icon: 'ClipboardCheck',
    mode: 'live',
    status: 'available',
    category: 'operational-tools',
    apiRequired: false, // Full API support via AuditsController
  },
  insights: {
    id: 'insights',
    name: 'Insights',
    description: 'Data visualization and trend analysis',
    icon: 'BarChart3',
    mode: 'demo',
    status: 'available',
    category: 'analytics-reporting',
    apiRequired: true, // No backend controller found - demo only
  },
  assetHistory: {
    id: 'assetHistory',
    name: 'Asset History',
    description: 'Complete historical record of all asset activities',
    icon: 'History',
    mode: 'live',
    status: 'available',
    category: 'analytics-reporting',
    apiRequired: false, // Full API support via AuditController
  },
  auditTrail: {
    id: 'auditTrail',
    name: 'Audit Trail',
    description: 'Comprehensive logging of all system changes',
    icon: 'FileText',
    mode: 'live',
    status: 'available',
    category: 'analytics-reporting',
    apiRequired: false, // Full API support via AuditController
  },
  importAssets: {
    id: 'importAssets',
    name: 'Import Assets',
    description: 'Bulk asset data upload functionality',
    icon: 'Upload',
    mode: 'live',
    status: 'available',
    category: 'system-administration',
    apiRequired: false, // Full API support via ImportController
  },
  importTransactions: {
    id: 'importTransactions',
    name: 'Import Transactions',
    description: 'Bulk transaction processing',
    icon: 'FileUp',
    mode: 'demo',
    status: 'available',
    category: 'system-administration',
    apiRequired: true, // Limited API support for transaction imports
  },
  locations: {
    id: 'locations',
    name: 'Locations',
    description: 'Manage physical and logical locations',
    icon: 'MapPin',
    mode: 'live',
    status: 'available',
    category: 'configuration',
    apiRequired: false, // Full API support via SettingsController
  },
  categories: {
    id: 'categories',
    name: 'Categories',
    description: 'Asset categorisation structure',
    icon: 'FolderTree',
    mode: 'live',
    status: 'available',
    category: 'configuration',
    apiRequired: false, // Full API support via SettingsController
  },
  equipment: {
    id: 'equipment',
    name: 'Equipment',
    description: 'Equipment type definitions',
    icon: 'Wrench',
    mode: 'live',
    status: 'available',
    category: 'configuration',
    apiRequired: false, // Full API support via SettingsController
  },
  disposalReasons: {
    id: 'disposalReasons',
    name: 'Disposal Reasons',
    description: 'Predefined disposal categorisation',
    icon: 'Trash2',
    mode: 'live',
    status: 'available',
    category: 'configuration',
    apiRequired: false, // Full API support via SettingsController
  },
  help: {
    id: 'help',
    name: 'Help & Support',
    description: 'Documentation and support resources',
    icon: 'HelpCircle',
    mode: 'live',
    status: 'available',
    category: 'configuration',
    apiRequired: false, // Static content - no API needed
  },
  workItems: {
    id: 'workItems',
    name: 'Tasks',
    description: 'Task and work order management system',
    icon: 'CheckSquare',
    mode: 'live',
    status: 'available',
    category: 'operational-tools',
    apiRequired: false, // Full API support via WorkItemsController
    documentationUrl: '/docs/work-items',
  },
  teamSettings: {
    id: 'teamSettings',
    name: 'Team Settings',
    description: 'Manage teams, assign users, and control application access',
    icon: 'Users',
    mode: 'live',
    status: 'available',
    category: 'system-administration',
    apiRequired: false, // API will be created separately
    documentationUrl: '/docs/team-settings',
  },
});

export const useFeatureSettings = () => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [modules, setModules] = useState<Record<string, FeatureModule>>(() => {
    // Load from localStorage on initial mount
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        const storedVersion = parsed.version ?? '1.0.0';
        
        // Check if version has changed - if so, migrate to new defaults
        if (storedVersion !== CURRENT_VERSION) {
          console.log(`Migrating feature settings from ${storedVersion} to ${CURRENT_VERSION}`);
          const defaultModules = getDefaultModules();
          
          // Sync the audit trail mode with app-mode on initialization
          if (defaultModules.auditTrail) {
            localStorage.setItem('app-mode', defaultModules.auditTrail.mode);
          }
          
          return defaultModules;
        }
        
        const loadedModules = parsed.modules || getDefaultModules();
        
        // Sync the audit trail mode with app-mode on initialization
        if (loadedModules.auditTrail) {
          localStorage.setItem('app-mode', loadedModules.auditTrail.mode);
        }
        
        return loadedModules;
      } catch (e) {
        console.error('Failed to parse stored feature settings:', e);
      }
    }
    
    // If no stored settings, initialize app-mode to demo
    const defaultModules = getDefaultModules();
    if (defaultModules.auditTrail) {
      localStorage.setItem('app-mode', defaultModules.auditTrail.mode);
    }
    
    return defaultModules;
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [recentlyChanged, setRecentlyChanged] = useState<string | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  const [lastSavedModules, setLastSavedModules] = useState<Record<string, FeatureModule> | null>(null);
  
  // Track the initial state to detect changes
  const initialModulesRef = useRef<Record<string, FeatureModule> | null>(null);
  
  // Initialize the reference to track changes and mark as initialized
  useEffect(() => {
    if (initialModulesRef.current === null) {
      initialModulesRef.current = modules;
      // Mark as initialized after a microtask to ensure localStorage sync is complete
      Promise.resolve().then(() => setIsInitialized(true));
    }
  }, [modules]);

  // Save to localStorage whenever modules change (for offline cache)
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      modules,
      lastUpdated: new Date().toISOString(),
      version: CURRENT_VERSION,
    }));
  }, [modules]);

  // Clear recently changed highlight after animation
  useEffect(() => {
    if (recentlyChanged) {
      const timer = setTimeout(() => {
        setRecentlyChanged(null);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [recentlyChanged]);

  const switchMode = useCallback(async (
    moduleId: string,
    newMode: FeatureMode,
    userId?: string
  ): Promise<boolean> => {
    setIsLoading(true);
    
    try {
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 500));

      setModules(prev => ({
        ...prev,
        [moduleId]: {
          ...prev[moduleId],
          mode: newMode,
          lastSwitched: new Date(),
          switchedBy: userId ?? 'current-user',
        },
      }));

      setRecentlyChanged(moduleId);
      setIsDirty(true);
      
      // If this is the audit trail module, update the global app-mode
      // This ensures the audit screens use the correct data source
      if (moduleId === 'auditTrail') {
        localStorage.setItem('app-mode', newMode);
        console.log(`Audit Trail mode changed to: ${newMode}`);
      }
      
      // Log the change for audit purposes
      const changeRequest: FeatureModeChangeRequest = {
        moduleId,
        fromMode: modules[moduleId].mode,
        toMode: newMode,
        timestamp: new Date(),
        userId,
      };
      
      console.log('Feature mode changed:', changeRequest);
      
      return true;
    } catch (error) {
      console.error('Failed to switch mode:', error);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [modules]);

  const switchAllToMode = useCallback(async (mode: FeatureMode): Promise<boolean> => {
    setIsLoading(true);
    
    try {
      await new Promise(resolve => setTimeout(resolve, 800));

      const updatedModules = { ...modules };
      Object.keys(updatedModules).forEach(key => {
        if (updatedModules[key].status === 'available' && !updatedModules[key].apiRequired) {
          updatedModules[key] = {
            ...updatedModules[key],
            mode,
            lastSwitched: new Date(),
          };
        }
      });

      setModules(updatedModules);
      setIsDirty(true);
      return true;
    } catch (error) {
      console.error('Failed to switch all modes:', error);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [modules]);

  const getModulesByCategory = useCallback((category: string) => {
    return Object.values(modules).filter(module => module.category === category);
  }, [modules]);

  const resetToDefaults = useCallback(() => {
    const defaultModules = getDefaultModules();
    setModules(defaultModules);
    setIsDirty(true);
    localStorage.removeItem(STORAGE_KEY);
    
    // Reset app-mode to the default audit trail mode
    if (defaultModules.auditTrail) {
      localStorage.setItem('app-mode', defaultModules.auditTrail.mode);
    }
  }, []);

  // Save settings to the backend API
  const saveSettings = useCallback(async (): Promise<boolean> => {
    setIsSaving(true);
    
    try {
      const settingsData = {
        modules,
        lastUpdated: new Date().toISOString(),
        version: CURRENT_VERSION,
      };
      
      // Save to API using authenticated dataService
      try {
        await dataService.saveFeatureSettings(settingsData);
      } catch (apiError) {
        console.error('Failed to save settings to API:', apiError);
        return false;
      }
      
      // Save to localStorage as cache after successful API save
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settingsData));
      
      // Ensure app-mode is synced with audit trail mode
      if (modules.auditTrail) {
        localStorage.setItem('app-mode', modules.auditTrail.mode);
      }
      
      setLastSavedModules(modules);
      setIsDirty(false);
      initialModulesRef.current = modules;
      
      return true;
    } catch (error) {
      console.error('Failed to save settings:', error);
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [modules]);

  // Discard unsaved changes and revert to last saved state
  const discardChanges = useCallback(() => {
    let revertedModules: Record<string, FeatureModule>;
    
    if (lastSavedModules) {
      revertedModules = lastSavedModules;
      setModules(lastSavedModules);
    } else if (initialModulesRef.current) {
      revertedModules = initialModulesRef.current;
      setModules(initialModulesRef.current);
    } else {
      revertedModules = getDefaultModules();
      setModules(revertedModules);
    }
    
    // Sync app-mode with the reverted audit trail mode
    if (revertedModules.auditTrail) {
      localStorage.setItem('app-mode', revertedModules.auditTrail.mode);
    }
    
    setIsDirty(false);
  }, [lastSavedModules]);

  // Load settings from the backend API
  const loadSettings = useCallback(async (): Promise<boolean> => {
    setIsLoading(true);
    
    try {
      // Try to load from API using authenticated dataService
      try {
        const data = await dataService.getFeatureSettings();
        if (data.modules) {
          setModules(data.modules as Record<string, FeatureModule>);
          setLastSavedModules(data.modules as Record<string, FeatureModule>);
          initialModulesRef.current = data.modules as Record<string, FeatureModule>;
          setIsDirty(false);
          return true;
        }
      } catch (apiError) {
        console.warn('API not available, using localStorage:', apiError);
      }
      
      // Fall back to localStorage
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed.modules) {
          setModules(parsed.modules);
          setLastSavedModules(parsed.modules);
          initialModulesRef.current = parsed.modules;
          setIsDirty(false);
          return true;
        }
      }
      
      // Use defaults if nothing is stored
      const defaults = getDefaultModules();
      setModules(defaults);
      setLastSavedModules(defaults);
      initialModulesRef.current = defaults;
      setIsDirty(false);
      
      return true;
    } catch (error) {
      console.error('Failed to load settings:', error);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    modules,
    isInitialized,
    isLoading,
    isSaving,
    isDirty,
    recentlyChanged,
    switchMode,
    switchAllToMode,
    getModulesByCategory,
    resetToDefaults,
    saveSettings,
    discardChanges,
    loadSettings,
  };
};
